import { useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectLoadedUserFilesById } from '@/user_files/files/store/selectors'
import { fetchAndStoreUserFiles } from '@/user_files/files/utils/fetch-and-store-user-files'
import { UserFileRead } from '@/uppy-file-upload/schemas'

type UseUserFilesResult = {
  filesById: Record<string, UserFileRead>
  loading: boolean
  error: boolean
}

/**
 * Gets user-file records for a list of ids.
 *
 * Reuses files already in the store (browsed folders + the by-id cache) and fetches
 * the rest in one request, writing them back to the cache. Use it wherever you have
 * file ids but need the full record, like the citation viewer or the selected-file chips.
 */
export function useUserFiles(ids: string[]): UseUserFilesResult {
  const dispatch = useAppDispatch()
  const loaded = useAppSelector(selectLoadedUserFilesById)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  // `ids` is a new array every render. Turn it into a stable string (deduped + sorted)
  // and derive uniqueIds from that, so the memo only recomputes when the ids actually change.
  const idsKey = [...new Set(ids)].sort().join(',')
  const uniqueIds = useMemo(() => (idsKey ? idsKey.split(',') : []), [idsKey])

  // The ids we don't have in the store yet.
  const missingKey = uniqueIds
    .filter((id) => !loaded[id])
    .sort()
    .join(',')

  useEffect(() => {
    const missing = missingKey ? missingKey.split(',') : []
    if (missing.length === 0) {
      // Clear stale state so a fetch superseded by an external store write (which
      // satisfies the same ids) doesn't leave the hook stuck on `loading=true`.
      setLoading(false)
      setError(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(false)
    fetchAndStoreUserFiles(missing, dispatch)
      .then(() => {
        if (!cancelled) setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setError(true)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [missingKey, dispatch])

  const filesById = useMemo(() => {
    const out: Record<string, UserFileRead> = {}
    for (const id of uniqueIds) {
      if (loaded[id]) out[id] = loaded[id]
    }
    return out
  }, [uniqueIds, loaded])

  return { filesById, loading, error }
}

type UseUserFileResult = {
  file: UserFileRead | null
  loading: boolean
  error: boolean
}

/** Single-id version of useUserFiles. */
export function useUserFile(id: string | null): UseUserFileResult {
  const { filesById, loading, error } = useUserFiles(id ? [id] : [])
  return { file: id ? filesById[id] ?? null : null, loading, error }
}
